/**
 * `/crm/activities` — what has to be done: overdue, today, upcoming, or done,
 * yours or everybody's. The tab and the filter are in the URL.
 *
 * "Today" is the browser's today, sent to the server with every list — the
 * server does not know the reader's time zone.
 */
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { CRM_PERMISSIONS, type ActivityDto, type ActivityDue } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useActivityActions, withoutDone } from './actions';
import { ActivityDoneCheckbox } from './ActivityDoneCheckbox';
import { ActivityIcon } from './ActivityIcon';
import { listActivities } from './api';
import { formatDay } from './format';
import { NewActivityDialog } from './NewActivityDialog';

const TABS: readonly ActivityDue[] = ['overdue', 'today', 'upcoming', 'done'];

export default function ActivitiesPage() {
  const { t } = useTranslation('crm');
  const { can, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const due = TABS.find((one) => one === params.get('due')) ?? 'today';
  const everyone = params.get('everyone') === 'true';
  const ownerId = everyone ? undefined : user?.id;

  const list = useListQuery({
    defaultSort: 'dueDate',
    defaultOrder: due === 'done' ? 'desc' : 'asc',
    resetKey: `${due}:${everyone}`,
  });
  const activities = useAsync(
    () => listActivities({ ...list.query, due, ...(ownerId !== undefined ? { ownerId } : {}) }),
    [list.key, due, ownerId],
  );
  const actions = useActivityActions({ onChanged: () => activities.reload() });

  const [creating, setCreating] = useState(false);

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  };

  return (
    <>
      <PageHeader
        title={t('activity.title')}
        description={t('activity.description')}
        actions={
          can(CRM_PERMISSIONS.WRITE) && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              {t('activity.new')}
            </Button>
          )
        }
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs value={due} onChange={(_event, value: ActivityDue) => update({ due: value })}>
          {TABS.map((one) => (
            <Tab key={one} value={one} label={t(`due.${one}`)} />
          ))}
        </Tabs>
        <FormControlLabel
          control={
            <Switch size="small" checked={everyone} onChange={(event) => update({ everyone: event.target.checked ? 'true' : null })} />
          }
          label={t('activity.everyone')}
        />
      </Stack>

      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={activities}>
        {({ activities: rows, page }) => (
          <EntityTable<ActivityDto>
            rows={rows}
            rowKey={(activity) => activity.id}
            list={list}
            page={page}
            empty={{
              icon: <EventAvailableOutlinedIcon fontSize="inherit" />,
              title: t(`activity.empty.${due}`),
            }}
            columns={[
              {
                field: 'done',
                label: '',
                width: 48,
                render: (activity) => <ActivityDoneCheckbox activity={activity} rowActions={actions.rowActions} />,
              },
              {
                field: 'subject',
                label: t('field.subject'),
                render: (activity) => (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <ActivityIcon type={activity.type} color="action" titleAccess={t(`activityType.${activity.type}`)} />
                    <Typography variant="body2" sx={{ textDecoration: activity.doneAt ? 'line-through' : 'none' }}>
                      {activity.subject}
                    </Typography>
                  </Stack>
                ),
              },
              {
                field: 'dueDate',
                label: t('field.dueDate'),
                sortable: true,
                width: 160,
                render: (activity) => (
                  <Typography variant="body2" color={due === 'overdue' ? 'error' : 'text.primary'}>
                    {formatDay(activity.dueDate)}
                    {activity.dueTime ? ` ${activity.dueTime}` : ''}
                  </Typography>
                ),
              },
              {
                field: 'regarding',
                label: t('activity.regarding'),
                render: (activity) => (
                  <Box>
                    {activity.deal && (
                      <Link component={RouterLink} to={`/crm/deals/${activity.deal.id}`} underline="hover" sx={{ display: 'block' }}>
                        {activity.deal.name}
                      </Link>
                    )}
                    {activity.person && (
                      <Link component={RouterLink} to={`/crm/persons/${activity.person.id}`} underline="hover" variant="caption" color="text.secondary">
                        {activity.person.name}
                      </Link>
                    )}
                  </Box>
                ),
              },
              { field: 'owner', label: t('field.owner'), width: 170, render: (activity) => activity.owner.name },
            ]}
            actions={(activity) => withoutDone(actions.rowActions(activity))}
            actionsWidth={100}
          />
        )}
      </AsyncContent>
      {actions.dialogs}

      <NewActivityDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => activities.reload()}
      />
    </>
  );
}
